import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { Link } from 'react-router-dom';
import MuiLink from '@material-ui/core/Link';
import Address from '../Address';
import Autocomplete from '../Autocomplete';
import Form from '../Form';
import TextField from '../TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Box from '@material-ui/core/Box';
import translations from './translations';
import getFullName from '@afosto/utils/getFullName';
import useForm from '@afosto/utils-react/validation/useForm';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import validationSchema from './validations';
import useRegisterInput from '../Form/useRegisterInput';

const ContactForm = ({ contact, onSubmit, onCancel, inline }) => {
  const intl = useIntl();
  const primaryBillingAddress = contact?.billing?.primary;
  const primaryShippingAddress = contact?.shipping?.primary;
  const primaryPhoneNumber = contact?.phone_numbers?.primary;
  const primaryOrganisation = contact?.organisations?.primary;
  const form = useForm({
    validationSchema,
    editInline: inline,
    defaultValues: {
      id: contact?.id ?? null,
      audience: contact?.audience ?? '',
      givenName: contact?.given_name ?? '',
      familyName: contact?.family_name ?? '',
      additionalName: contact?.additional_name ?? '',
      email: contact?.email ?? '',
      tags: contact?.tags ?? [],
    },
    onSubmit,
    onCancel,
  });
  const { setIsEditing, triggerValidation } = form;

  useEffect(() => {
    setIsEditing(false);
  }, [contact, setIsEditing]);

  useRegisterInput('id', true, form);

  return (
    <LoadingWrapper isLoading={form.isSubmitting}>
      <Form form={form}>
        <TextField
          name="givenName"
          label={intl.formatMessage(translations.fields.firstName.label)}
          readOnly={form.editInline}
        />
        <TextField
          name="additionalName"
          label={intl.formatMessage(translations.fields.lastNamePrefix.label)}
          readOnly={form.editInline}
        />
        <TextField
          name="familyName"
          label={intl.formatMessage(translations.fields.lastName.label)}
          readOnly={form.editInline}
        />
        <TextField
          name="email"
          label={intl.formatMessage(translations.fields.email.label)}
          type="email"
          readOnly={!!contact?.id}
          inline={form.editInline || !!contact?.id}
          onBlur={() => {
            if (form.submitted && !inline) {
              triggerValidation('email');
            }
          }}
        />
        <TextField
          name="audience"
          label={intl.formatMessage(translations.fields.audience.label)}
          readOnly={form.editInline}
        />
        <Autocomplete
          name="tags"
          label={intl.formatMessage(translations.fields.tags.label)}
          multiple
          enableCreate
          options={[]}
          readOnly={form.editInline}
        />

        {inline && (
          <>
            <FormControl variant="outlined" margin="normal" disabled fullWidth>
              <InputLabel shrink variant="outlined">
                {intl.formatMessage(translations.fields.primaryBillingAddress.label)}
              </InputLabel>
              <Box p={2} pt={3}>
                {primaryBillingAddress ? (
                  <Address
                    company={primaryBillingAddress.organisation}
                    fullName={getFullName(primaryBillingAddress)}
                    addressLine1={primaryBillingAddress.address_line_1}
                    addressLine2={primaryBillingAddress.address_line_2}
                    postalCode={primaryBillingAddress.postal_code}
                    city={primaryBillingAddress.locality}
                    country={primaryBillingAddress.country_code}
                    administrativeArea={primaryBillingAddress.administrative_area}
                  />
                ) : (
                  '-'
                )}
              </Box>
            </FormControl>
            <FormControl variant="outlined" margin="normal" disabled fullWidth>
              <InputLabel shrink variant="outlined">
                {intl.formatMessage(translations.fields.primaryShippingAddress.label)}
              </InputLabel>
              <Box p={2} pt={3}>
                {primaryShippingAddress ? (
                  <Address
                    company={primaryShippingAddress.organisation}
                    fullName={getFullName(primaryShippingAddress)}
                    addressLine1={primaryShippingAddress.address_line_1}
                    addressLine2={primaryShippingAddress.address_line_2}
                    postalCode={primaryShippingAddress.postal_code}
                    city={primaryShippingAddress.locality}
                    country={primaryShippingAddress.country_code}
                    administrativeArea={primaryShippingAddress.administrative_area}
                  />
                ) : (
                  '-'
                )}
              </Box>
            </FormControl>
            <FormControl variant="outlined" margin="normal" disabled fullWidth>
              <InputLabel shrink variant="outlined">
                {intl.formatMessage(translations.fields.primaryPhoneNumber.label)}
              </InputLabel>
              <Box p={2} pt={3}>
                {primaryPhoneNumber?.number ?? '-'}
              </Box>
            </FormControl>
            <FormControl variant="outlined" margin="normal" disabled fullWidth>
              <InputLabel shrink variant="outlined">
                {intl.formatMessage(translations.fields.primaryOrganisation.label)}
              </InputLabel>
              <Box p={2} pt={3}>
                {primaryOrganisation ? (
                  <MuiLink
                    variant="subtitle1"
                    component={Link}
                    to={`/organisations/${primaryOrganisation.id}`}
                  >
                    {primaryOrganisation.name}
                  </MuiLink>
                ) : (
                  '-'
                )}
              </Box>
            </FormControl>
          </>
        )}
      </Form>
    </LoadingWrapper>
  );
};

ContactForm.propTypes = {
  contact: PropTypes.object,
  inline: PropTypes.bool,
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
};

ContactForm.defaultProps = {
  contact: {},
  inline: false,
  onCancel: undefined,
  onSubmit: undefined,
};

export default ContactForm;
